import React, { useState } from 'react';
import { HiOutlineX } from 'react-icons/hi';

/**
 * Multi-assignee picker.
 * value: array of { userId?, externalName? }
 * onChange: (newArray) => void
 *
 * UI: existing assignees as chips; an inline user-search dropdown to add more;
 * a small text input + Add button for external (non-user) names.
 */
const AssigneePicker = ({
  value = [],
  onChange,
  systemUsers = [],
  placeholder = 'Add assignee…',
  size = 'md',
  compact = false
}) => {
  const [search, setSearch] = useState('');
  const [externalDraft, setExternalDraft] = useState('');

  const lookupUser = (id) => systemUsers.find(u => u._id === id);

  const filtered = search.trim()
    ? systemUsers.filter(u =>
        (u.fullName || u.username || '').toLowerCase().includes(search.toLowerCase()) &&
        !value.some(v => v.userId === u._id || (v.userId && v.userId._id === u._id))
      ).slice(0, 6)
    : [];

  const addUser = (u) => {
    onChange([...value, { userId: u._id }]);
    setSearch('');
  };

  const addExternal = () => {
    const name = externalDraft.trim();
    if (!name) return;
    onChange([...value, { externalName: name }]);
    setExternalDraft('');
  };

  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));

  const fontSize = compact ? 11 : (size === 'sm' ? 12 : 13);
  const chipPad = compact ? '1px 6px' : '3px 8px';

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
      {value.map((v, i) => {
        const u = v.userId && (typeof v.userId === 'object' ? v.userId : lookupUser(v.userId));
        const label = u ? (u.fullName || u.username) : (v.externalName || '?');
        const isExt = !v.userId;
        return (
          <span key={i} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: isExt ? '#fef3c7' : '#e0e7ff',
            color: isExt ? '#92400e' : '#3730a3',
            padding: chipPad, borderRadius: 99, fontSize, fontWeight: 500
          }}>
            {label}
            <button type="button" onClick={() => removeAt(i)} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 0, color: 'inherit', display: 'flex'
            }}>
              <HiOutlineX size={compact ? 10 : 12} />
            </button>
          </span>
        );
      })}

      {/* User search */}
      <div style={{ position: 'relative', minWidth: 110 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={value.length === 0 ? placeholder : 'Add user…'}
          style={{
            fontSize, padding: compact ? '2px 6px' : '4px 8px',
            border: '1px solid #e2e8f0', borderRadius: 4, width: '100%', boxSizing: 'border-box'
          }}
        />
        {filtered.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 60,
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)', marginTop: 2, overflow: 'hidden', minWidth: 180
          }}>
            {filtered.map(u => (
              <div key={u._id} onClick={() => addUser(u)}
                style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 12, color: '#1e293b', borderBottom: '1px solid #f1f5f9' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <span style={{ fontWeight: 600 }}>{u.fullName || u.username}</span>
                {u.username && u.fullName && <span style={{ color: '#94a3b8', marginLeft: 4, fontSize: 10 }}>@{u.username}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* External name */}
      <input
        value={externalDraft}
        onChange={e => setExternalDraft(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addExternal(); } }}
        placeholder="external name…"
        style={{
          fontSize, padding: compact ? '2px 6px' : '4px 8px',
          border: '1px dashed #cbd5e1', borderRadius: 4, minWidth: 90
        }}
      />
      {externalDraft.trim() && (
        <button type="button" onClick={addExternal}
          style={{ fontSize, padding: compact ? '2px 8px' : '4px 10px', background: '#fbbf24', color: '#78350f', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
          + Ext
        </button>
      )}
    </div>
  );
};

export default AssigneePicker;
